import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { 
    FaChartPie, 
    FaSpinner, 
    FaExclamationTriangle,
    FaTasks,
    FaCheckCircle,
    FaTag
} from 'react-icons/fa';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import { 
    COLORS, 
    PanelContent, 
    PanelTitle,
    StatGrid,
    StatCard,
} from './styles';

const SectionLabel = styled.div`
    color: ${COLORS.muted};
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 8px 0 6px 0;
    display: flex;
    align-items: center;
    gap: 6px;
    
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: ${COLORS.border};
    }
`;

const ChartAndCasesWrapper = styled.div`
    display: flex;
    gap: 12px;
    flex: 1;
    min-height: 0;
    
    @media screen and (max-width: 1920px) {
        gap: 8px;
    }
`;

const ChartSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const CasesSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    max-width: 50%;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${COLORS.muted};
    text-align: center;
    padding: 10px;
    font-size: 11px;
    
    svg {
        font-size: 24px;
        margin-bottom: 8px;
        opacity: 0.5;
    }
`;

const FilterToggle = styled.button`
    background: ${props => props.$active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${props => props.$active ? '#10b981' : COLORS.muted};
    border: 1px solid ${props => props.$active ? 'rgba(16, 185, 129, 0.4)' : COLORS.border};
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    
    &:hover {
        background: ${props => props.$active ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
        border-color: ${props => props.$active ? 'rgba(16, 185, 129, 0.5)' : COLORS.borderAccent};
    }
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const LabelRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 8px;
    border-radius: 6px;
    transition: background 0.2s;
    &:hover {
        background: rgba(255, 255, 255, 0.05);
    }
`;

const LabelDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
`;

const LabelName = styled.span`
    font-size: 11px;
    color: ${COLORS.light};
    flex: 1;
    margin-left: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @media screen and (max-width: 1920px) { font-size: 10px; }
`;

const LabelCount = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${COLORS.light};
    min-width: 28px;
    text-align: right;
    @media screen and (max-width: 1920px) { font-size: 12px; }
`;

const LabelPercent = styled.span`
    font-size: 9px;
    font-weight: 600;
    min-width: 32px;
    text-align: right;
    margin-left: 4px;
    @media screen and (max-width: 1920px) { font-size: 8px; }
`;

const LabelsContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 4px;
    &::-webkit-scrollbar { width: 3px; }
    &::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.2); border-radius: 2px; }
    &::-webkit-scrollbar-thumb { background: rgba(100, 150, 200, 0.3); border-radius: 2px; }
`;

/**
 * AdviceStatusOverview Component (Merged)
 * 
 * Displays real-time case status metrics, pie chart, AND active cases progress
 * Now combines the previous CaseProgressOverview functionality
 */
const AdviceStatusOverview = () => {
    const authenticatedAxios = useAuthenticatedAxios();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartRadius, setChartRadius] = useState(40);
    const [leadsOnly, setLeadsOnly] = useState(false);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const statsRes = await authenticatedAxios.get(`dashboard/stats?${leadsOnly ? 'filter=leads' : ''}`);
                setStats(statsRes.data);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [authenticatedAxios, leadsOnly]);
    
    useEffect(() => {
        const updateChartDimensions = () => {
            if (window.innerWidth <= 1366) {
                setChartRadius(28);
            } else if (window.innerWidth <= 1920) {
                setChartRadius(32);
            } else {
                setChartRadius(40);
            }
        };
        
        updateChartDimensions();
        window.addEventListener('resize', updateChartDimensions);
        
        return () => {
            window.removeEventListener('resize', updateChartDimensions);
        };
    }, []);

    // Loading state
    if (loading) {
        return (
            <PanelContent>
                <PanelTitle><FaChartPie /> Case Status Overview</PanelTitle>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    color: COLORS.muted 
                }}>
                    <FaSpinner className="spin" style={{ marginRight: 8, animation: 'spin 1s linear infinite' }} />
                    Loading...
                </div>
                <style>{`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </PanelContent>
        );
    }

    // Error state
    if (error) {
        return (
            <PanelContent>
                <PanelTitle><FaChartPie /> Case Status Overview</PanelTitle>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    color: COLORS.warning,
                    textAlign: 'center',
                    padding: 20
                }}>
                    <FaExclamationTriangle style={{ fontSize: 24, marginBottom: 8 }} />
                    <div style={{ fontSize: 12 }}>{error}</div>
                </div>
            </PanelContent>
        );
    }

    // Prepare chart data
    const leadLabelData = (stats?.leadLabelStats || []).filter(item => item.value > 0);
    const totalLabeled = leadLabelData.reduce((sum, item) => sum + item.value, 0);
    const hasLabelData = leadLabelData.length > 0;
    const progressSummary = stats?.progressSummary || {};
    
    return (
        <PanelContent>
            <TitleRow>
                <PanelTitle style={{ marginBottom: 0 }}><FaChartPie /> Case Status Overview</PanelTitle>
                <FilterToggle 
                    $active={leadsOnly} 
                    onClick={() => setLeadsOnly(prev => !prev)}
                >
                    {leadsOnly ? 'Leads Only' : 'All'}
                </FilterToggle>
            </TitleRow>
            
            <StatGrid $cols={4}>
                <StatCard>
                    <div className="stat-title">Opened</div>
                    <div className="stat-value" style={{ fontSize: 16 }}>{stats?.widgetOpenedCount || 0}</div>
                </StatCard>
                
                <StatCard>
                    <div className="stat-title">Active</div>
                    <div className="stat-value" style={{ color: '#7c3aed', fontSize: 16 }}>{stats?.inProgress || 0}</div>
                </StatCard>
                
                <StatCard>
                    <div className="stat-title">Done</div>
                    <div className="stat-value" style={{ color: '#059669', fontSize: 16 }}>{stats?.completed || 0}</div>
                </StatCard>
                
                <StatCard>
                    <div className="stat-title">Avg %</div>
                    <div className="stat-value" style={{ color: COLORS.info, fontSize: 16 }}>
                        {progressSummary.avgProgressPercentage || 0}%
                    </div>
                </StatCard>
            </StatGrid>
            
            {/* Lead Label Distribution */}
            <ChartAndCasesWrapper>
                {/* Pie Chart Section */}
                <ChartSection>
                    <SectionLabel>
                        <FaTag style={{ fontSize: 9 }} /> Lead Labels
                    </SectionLabel>
                    
                    {hasLabelData ? (
                        <div style={{ flex: 1, minHeight: 80 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={leadLabelData}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={chartRadius}
                                        innerRadius={chartRadius * 0.5}
                                        fill="#8884d8"
                                        dataKey="value"
                                        nameKey="name"
                                        isAnimationActive={true}
                                    >
                                        {leadLabelData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color || COLORS.chart[index % COLORS.chart.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip 
                                        formatter={(value, name) => [
                                            `${value} (${totalLabeled > 0 ? Math.round(value / totalLabeled * 100) : 0}%)`, 
                                            name
                                        ]} 
                                        contentStyle={{
                                            background: COLORS.bgSecondary,
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: 6,
                                            padding: '6px 10px',
                                            fontSize: 11
                                        }}
                                        itemStyle={{ color: COLORS.light }}
                                        labelStyle={{ color: COLORS.light, fontWeight: 600 }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <EmptyState>
                            <FaTag />
                            <span>No label data yet</span>
                        </EmptyState>
                    )}
                </ChartSection>
                
                {/* Label Breakdown List */}
                <CasesSection>
                    <SectionLabel>
                        <FaTasks style={{ fontSize: 9 }} /> Breakdown
                    </SectionLabel>
                    
                    <LabelsContainer>
                        {hasLabelData ? (
                            leadLabelData.map((item, index) => {
                                const pct = totalLabeled > 0 ? Math.round((item.value / totalLabeled) * 100) : 0;
                                return (
                                    <LabelRow key={item.name || index}>
                                        <LabelDot style={{ background: item.color || COLORS.chart[index % COLORS.chart.length] }} />
                                        <LabelName>{item.name}</LabelName>
                                        <LabelCount>{item.value}</LabelCount>
                                        <LabelPercent style={{ color: item.color || COLORS.muted }}>{pct}%</LabelPercent>
                                    </LabelRow>
                                );
                            })
                        ) : (
                            <EmptyState>
                                <FaCheckCircle />
                                <span>No labels assigned</span>
                            </EmptyState>
                        )}
                    </LabelsContainer>
                </CasesSection>
            </ChartAndCasesWrapper>
            
            {/* Recent activity footer */}
            {stats?.recentActivity && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    marginTop: 8,
                    padding: '6px 0',
                    borderTop: `1px solid ${COLORS.border}`,
                    fontSize: 10,
                    color: COLORS.muted
                }}>
                    <span>Today: <strong style={{ color: '#059669' }}>{stats.recentActivity.today}</strong></span>
                    <span>Week: <strong style={{ color: '#7c3aed' }}>{stats.recentActivity.thisWeek}</strong></span>
                    <span>Month: <strong style={{ color: '#4f46e5' }}>{stats.recentActivity.thisMonth}</strong></span>
                </div>
            )}
        </PanelContent>
    );
};

export default AdviceStatusOverview;
